import {Dispatch, MutableRefObject, RefObject, SetStateAction, useEffect, useRef} from "react"
import {I18nIndividual as GedcomIndividual} from "../model/gedcom-individual"
import {I18nFamily as GedcomFamily} from "../model/gedcom-family"
import {validate as isUUID} from "uuid"
import {maxRowLimit, startId} from "../creator/creator"

export const shortenId = (rowId: string) => rowId.slice(0, 5)

export const restoredSession = (() => {
    try {
        const restoredData = JSON.parse(localStorage.getItem("creatorData") || sessionStorage.getItem("creatorData") || "{}")
        return { restoredIndividuals: (restoredData.savedIndividualsData?.length ?? 0) > 0, restoredFamilies: (restoredData.savedFamiliesData?.length ?? 0) > 0 }
    } catch {
        return { restoredIndividuals: false, restoredFamilies: false}
    }
})

export function createRow<T extends GedcomIndividual | GedcomFamily>(
    lastId: MutableRefObject<number>,
    rowCount: number,
    rowType: { empty: (id: number) => T }
): T {
    if (rowCount > maxRowLimit) throw new Error("Individuals limit reached: 9999")
    const nextId = lastId.current + 1
    const newRow = rowType.empty(nextId)
    if (/^[IF](\d+)$/.test(newRow.id))
        lastId.current = nextId  // Only increment lastId if genId was Creator type (not UUID)
    return newRow
}

export function removeRow<T extends GedcomIndividual | GedcomFamily>(
    rowIndex: number,
    lastId: MutableRefObject<number>,
    setData: Dispatch<SetStateAction<T[]>>,
    rowType: { empty: (id: number) => T }
): void {
    setData(prev => {
        const updated = [...prev]
        updated.splice(rowIndex, 1)
        lastId.current = maxId<T>(updated)  // Recompute numeric lastId from remaining rows
        if (updated.length === 0) {
            lastId.current++
            return [rowType.empty(lastId.current)]  // If it was the last one, add a new placeholder
        }
        return updated
    })
}

export function shiftIdsAfter<T extends GedcomIndividual | GedcomFamily>(
    rowType: typeof GedcomIndividual | typeof GedcomFamily,
    rowIndex: number,
    rowVersion: MutableRefObject<number>,
    setData: Dispatch<SetStateAction<T[]>>
) {
    setData(prev => {
        const updated = [...prev]
        const prevNum = rowIndex > 0 ? parseInt(updated[rowIndex - 1].id.replace(rowType.prefix, "")) : -1
        let nextNum = prevNum + 1
        // If all the following rows are untouched, shift the ID vacant from the last removeRow
        for (let i = rowIndex; i < updated.length; i++) {
            if (!updated[i].pristine) break
            updated[i].id = `${rowType.prefix}${nextNum}`
            nextNum++
            rowVersion.current++
        }
        return updated
    })
}

export function maxId<T extends GedcomIndividual | GedcomFamily>(data: T[]): number {
    return data.reduce((max, row) => {
        const match = row.id.match(/^[IF](\d+)$/)
        const numId = parseInt(match![1])
        return Math.max(max, numId)
    }, startId)
}

/**
 * Auto-scroll hook.
 */
export function useAutoScroll(scrollRef: RefObject<HTMLDivElement>, dataLength: number) {
    const preloadDone = useRef(false)
    const prevLength = useRef(dataLength)
    useEffect(() => {
        if (!preloadDone.current) {  // Skip scrolling on first render
            preloadDone.current = true
            prevLength.current = dataLength
            return
        }
        // Only scroll after adding rows (not after removal)
        if (dataLength > prevLength.current) {
            if (scrollRef.current) {
                scrollRef.current.scrollTop = scrollRef.current.scrollHeight
            }
        }
        prevLength.current = dataLength
    }, [dataLength, scrollRef])
}

/**
 * Comparator for the "lowest" ID from two individual IDs for scrolling.
 */
export function lowestId(id1: string, id2: string): string | undefined {
    const numId1 = parseCreatorId(id1)
    const numId2 = parseCreatorId(id2)
    if (numId1 && numId2) return numId1 <= numId2 ? id1 : id2
    if (numId1 !== null) return id1
    if (numId2 !== null) return id2
    // Fallback: lexicographic
    return id1 <= id2 ? id1 : id2
}

const parseCreatorId = (id: string) => {
    if (isUUID(id)) return null
    const numericPart = id.replace(/^\D+/, "")
    return numericPart && !isNaN(Number(numericPart)) ? Number(numericPart) : null
}

/**
 * On every parent change, register parent-child bindings.
 */
export function setParentBindings(row: GedcomIndividual, parentField: string, parentId: string, individualsData: GedcomIndividual[], familiesData?: GedcomFamily[]) {
    if (!parentId || parentId === "") {
        // Unsetting parent -> find the one set (might not exist if unsetting) and decrease
        const parent = individualsData.find(i => i.id === row[parentField])
        if (parent) parent.numberOfChildren--
    } else
        // Setting parent -> find new one and increase
        individualsData.find(i => i.id === parentId)!.numberOfChildren++
    // May have already a linked family, but also the family record may be created later
    const fatherId = parentField === "fatherId" ? parentId : row.fatherId
    const motherId = parentField === "motherId" ? parentId : row.motherId
    if (familiesData && fatherId && motherId) {
        const family = familiesData.find(f => f.husbandId === fatherId && f.wifeId === motherId)
        if (family) {
            row.familyChild = family.id
            family.children.add(row.id)
        }
    }
}

/**
 * On parent change, register family bindings.
 * Call this too after using the start family button
 */
const spousePartnerField = { husbandId: "wifeId", wifeId: "husbandId" }

export function setFamilyBindings(row: GedcomFamily, individualsData: GedcomIndividual[], spouseField?: keyof GedcomIndividual | keyof GedcomFamily, spouseId?: string) {
    const individualsMap = new Map(individualsData.map(indi => [indi.id, indi]))
    if (spouseField) {
        singleSpouseChange(row, spouseField, individualsMap, spouseId)
    } else {
        fullFamilyChange(row, individualsData, individualsMap)
    }
}

function singleSpouseChange(row: GedcomFamily, spouseField: keyof GedcomIndividual | keyof GedcomFamily, individualsMap: Map<string, GedcomIndividual>, spouseId?: string) {
    const existingSpouse = row[spouseField] ? individualsMap.get(row[spouseField]) : undefined
    if (!spouseId || spouseId === "") {
        // Unset existing partner's bindings
        if (existingSpouse) {
            existingSpouse.numberOfMarriages--
            const partnerField = spousePartnerField[spouseField]
            const partnerId = row[partnerField]
            if (partnerId) {
                const counterpart = individualsMap.get(partnerId)
                if (counterpart) {
                    existingSpouse.familySpouses.delete(row.id)
                    counterpart.familySpouses.delete(row.id)
                }
            }
        }
        // Unset current spouse
        row[spouseField] = undefined as any
    } else {
        // Setting new spouse
        row[spouseField] = spouseId
        const newSpouse = individualsMap.get(spouseId)!
        if (!newSpouse.familySpouses.has(row.id)) newSpouse.numberOfMarriages++
        // Set partner binding (if exists)
        const partnerField = spousePartnerField[spouseField]
        const partner = row[partnerField] ? individualsMap.get(row[partnerField]) : undefined
        // If both exist, link family bindings
        if (newSpouse && partner) {
            partner.familySpouses.add(row.id)
            newSpouse.familySpouses.add(row.id)
        }
    }
}

function fullFamilyChange(row: GedcomFamily, individualsData: GedcomIndividual[], individualsMap: Map<string, GedcomIndividual>) {
    // Both spouses present
    let husband: GedcomIndividual | undefined
    if (row.husbandId) {
        husband = individualsMap.get(row.husbandId)
        if (husband && !husband.familySpouses.has(row.id)) husband.numberOfMarriages++
    }
    let wife: GedcomIndividual | undefined
    if (row.wifeId) {
        wife = individualsMap.get(row.wifeId)
        if (wife && !wife.familySpouses.has(row.id)) wife.numberOfMarriages++
    }
    // If both exist, link parent-children bindings
    if (husband && wife) {
        husband.familySpouses.add(row.id)
        wife.familySpouses.add(row.id)
        // Bind children
        for (const child of individualsData) {
            if (child.fatherId === row.husbandId && child.motherId === row.wifeId) {
                child.familyChild = row.id
                row.children.add(child.id)
            }
        }
    }
}

export function cascadeClearDependencies(
    row: GedcomIndividual,
    setIndividualsData: Dispatch<SetStateAction<GedcomIndividual[]>>,
    setFamiliesData: Dispatch<SetStateAction<GedcomFamily[]>>
) {
    // Cascade clear fatherId/motherId in parents
    setIndividualsData(prev => prev.map(indi => {
        if (indi.fatherId === row.id) indi.fatherId = undefined
        if (indi.motherId === row.id) indi.motherId = undefined
        return indi
    }))
    // Cascade clear husbandId/wifeId in families
    setFamiliesData(prev =>
        prev.map(fam => {
            if (fam.husbandId === row.id) fam.husbandId = undefined
            if (fam.wifeId === row.id) fam.wifeId = undefined
            return fam
        })
    )
}